/**
 * Notification Quiet Hours
 *
 * Shared delivery decisions for focus protection and recovery windows.
 *
 * Primary responsibilities:
 * - Decide whether a notification should wait for quiet hours to end
 * - Convert local quiet-hours windows into UTC scheduled delivery times
 * - Add explicit metadata explaining deferred delivery
 *
 * Dependencies:
 * - Intl DateTimeFormat
 * - Notification settings types
 */

import type { NotificationSettings } from '@/types/notifications'

export type QuietHoursDeliveryDecision =
  | {
      active: false
      timeZone: string
    }
  | {
      active: true
      resumeAt: Date
      timeZone: string
    }

type QuietHoursDeliveryPlan = {
  deferredAt: string
  kind: 'quiet_hours_send_later'
  reason: string
  resumeAt: string
  timeZone: string
}

type ParsedClockTime = {
  hour: number
  minute: number
}

type ZonedParts = {
  day: number
  hour: number
  minute: number
  month: number
  year: number
}

// --------------

function toMinutes(clock: ParsedClockTime) {
  return clock.hour * 60 + clock.minute
}

// --------------

function getOptionalValidTimeZone(timeZone: string | null | undefined) {
  if (!timeZone) {
    return null
  }

  const trimmedTimeZone = timeZone.trim()

  if (!trimmedTimeZone) {
    return null
  }

  try {
    new Intl.DateTimeFormat('en-US', {
      timeZone: trimmedTimeZone
    }).format(new Date())
    return trimmedTimeZone
  } catch {
    return null
  }
}

// --------------

function getEffectiveQuietHoursTimeZone(args: {
  fallbackTimeZone?: string | null
  primaryTimeZone?: string | null
}) {
  const primaryTimeZone = getOptionalValidTimeZone(args.primaryTimeZone)
  const fallbackTimeZone = getOptionalValidTimeZone(args.fallbackTimeZone)

  if (primaryTimeZone && primaryTimeZone !== 'UTC') {
    return primaryTimeZone
  }

  return fallbackTimeZone || primaryTimeZone || 'UTC'
}

// --------------

function parseClockTime(
  value: string | null | undefined,
  fallback: ParsedClockTime
): ParsedClockTime {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value || '')

  if (!match) {
    return fallback
  }

  const hour = Number.parseInt(match[1] || String(fallback.hour), 10)
  const minute = Number.parseInt(match[2] || String(fallback.minute), 10)

  return {
    hour: Math.min(23, Math.max(0, hour)),
    minute: Math.min(59, Math.max(0, minute))
  }
}

// --------------

function getZonedParts(date: Date, timeZone: string): ZonedParts {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    timeZone,
    year: 'numeric'
  })
  const partMap = new Map(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )

  return {
    day: Number.parseInt(partMap.get('day') || '1', 10),
    hour: Number.parseInt(partMap.get('hour') || '0', 10),
    minute: Number.parseInt(partMap.get('minute') || '0', 10),
    month: Number.parseInt(partMap.get('month') || '1', 10),
    year: Number.parseInt(partMap.get('year') || '1970', 10)
  }
}

// --------------

function getTimeZoneOffsetMs(date: Date, timeZone: string) {
  const parts = getZonedParts(date, timeZone)
  const zonedTimestamp = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute
  )

  return zonedTimestamp - date.getTime()
}

// --------------

function zonedPartsToUtcDate(parts: ZonedParts, timeZone: string) {
  const utcGuess = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute
  ))
  const firstOffset = getTimeZoneOffsetMs(utcGuess, timeZone)
  const firstMatch = new Date(utcGuess.getTime() - firstOffset)
  const secondOffset = getTimeZoneOffsetMs(firstMatch, timeZone)

  if (secondOffset !== firstOffset) {
    return new Date(utcGuess.getTime() - secondOffset)
  }

  return firstMatch
}

// --------------

function addDaysToZonedDate(parts: ZonedParts, days: number): ZonedParts {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days))

  return {
    ...parts,
    day: date.getUTCDate(),
    month: date.getUTCMonth() + 1,
    year: date.getUTCFullYear()
  }
}

// --------------

export function getQuietHoursDeliveryDecision(args: {
  fallbackTimeZone?: string | null
  quietHours: NotificationSettings['quietHours']
  referenceDate?: Date
}): QuietHoursDeliveryDecision {
  const timeZone = getEffectiveQuietHoursTimeZone({
    fallbackTimeZone: args.fallbackTimeZone,
    primaryTimeZone: args.quietHours.timezone
  })

  if (!args.quietHours.enabled) {
    return {
      active: false,
      timeZone
    }
  }

  const referenceDate = args.referenceDate ?? new Date()
  const localNow = getZonedParts(referenceDate, timeZone)
  const start = parseClockTime(args.quietHours.startTime, {
    hour: 22,
    minute: 0
  })
  const end = parseClockTime(args.quietHours.endTime, {
    hour: 8,
    minute: 0
  })
  const startMinutes = toMinutes(start)
  const endMinutes = toMinutes(end)
  const currentMinutes = localNow.hour * 60 + localNow.minute

  if (startMinutes === endMinutes) {
    return {
      active: false,
      timeZone
    }
  }

  const crossesMidnight = startMinutes > endMinutes
  const active = crossesMidnight
    ? currentMinutes >= startMinutes || currentMinutes < endMinutes
    : currentMinutes >= startMinutes && currentMinutes < endMinutes

  if (!active) {
    return {
      active: false,
      timeZone
    }
  }

  let resumeParts: ZonedParts = {
    ...localNow,
    hour: end.hour,
    minute: end.minute
  }

  if (crossesMidnight && currentMinutes >= startMinutes) {
    resumeParts = addDaysToZonedDate(resumeParts, 1)
  }

  let resumeAt = zonedPartsToUtcDate(resumeParts, timeZone)

  if (resumeAt.getTime() <= referenceDate.getTime()) {
    resumeParts = addDaysToZonedDate(resumeParts, 1)
    resumeAt = zonedPartsToUtcDate(resumeParts, timeZone)
  }

  return {
    active: true,
    resumeAt,
    timeZone
  }
}

// --------------

export function buildQuietHoursDeferredData(args: {
  data: Record<string, unknown>
  decision: Extract<QuietHoursDeliveryDecision, { active: true }>
  referenceDate?: Date
}) {
  const deferredAt = args.referenceDate ?? new Date()
  const deliveryPlan: QuietHoursDeliveryPlan = {
    deferredAt: deferredAt.toISOString(),
    kind: 'quiet_hours_send_later',
    reason: 'Non-urgent notification held for quiet hours',
    resumeAt: args.decision.resumeAt.toISOString(),
    timeZone: args.decision.timeZone
  }

  return {
    ...args.data,
    deliveryPlan
  }
}
